import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, IconButton, Alert, Switch, FormControlLabel, MenuItem,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { regionsAPI, providersAPI } from '../../services/api';
import ConfirmDialog from '../../components/ConfirmDialog';
import { Region, Provider } from '../../types';

interface RegionFormData {
  regionName: string;
  regionCode: string;
  providerId: number | string;
  isActive: boolean;
  displayOrder: number;
}

const Regions = () => {
  const [regions, setRegions] = useState<Region[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRegion, setEditingRegion] = useState<Region | null>(null);
  const [formData, setFormData] = useState<RegionFormData>({
    regionName: '', regionCode: '', providerId: '', isActive: true, displayOrder: 0,
  });
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; id: number }>({ open: false, id: 0 });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [regionsRes, providersRes] = await Promise.all([regionsAPI.getAll(), providersAPI.getAll()]);
      setRegions(regionsRes.data);
      setProviders(providersRes.data.filter((p: Provider) => p.IsActive));
      setError('');
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (region: Region | null = null) => {
    if (region) {
      setEditingRegion(region);
      setFormData({
        regionName: region.RegionName, regionCode: region.RegionCode,
        providerId: region.ProviderId, isActive: !!region.IsActive, displayOrder: region.DisplayOrder,
      });
    } else {
      setEditingRegion(null);
      setFormData({
        regionName: '', regionCode: '', providerId: providers[0]?.ProviderId || '', isActive: true, displayOrder: 0
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingRegion(null);
    setFormData({ regionName: '', regionCode: '', providerId: '', isActive: true, displayOrder: 0 });
  };

  const handleSubmit = async () => {
    try {
      if (editingRegion) {
        await regionsAPI.update(editingRegion.RegionId, formData);
      } else {
        await regionsAPI.create(formData);
      }
      fetchData();
      handleCloseDialog();
      setError('');
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setError(axiosErr.response?.data?.error || 'Failed to save region');
    }
  };

  const handleDeleteClick = (id: number) => {
    setConfirmDialog({ open: true, id });
  };

  const handleDeleteConfirm = async () => {
    const id = confirmDialog.id;
    setConfirmDialog({ open: false, id: 0 });
    try {
      await regionsAPI.delete(id);
      fetchData();
      setError('');
    } catch (err) {
      setError('Failed to delete region');
    }
  };

  if (loading) return <Typography>Loading...</Typography>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Regions</Typography>
        <Button variant="contained" color="secondary" size="small" startIcon={<AddIcon fontSize="small" />} onClick={() => handleOpenDialog()}>
          Add Region
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Region Name</TableCell>
              <TableCell>Region Code</TableCell>
              <TableCell>Provider</TableCell>
              <TableCell>Active</TableCell>
              <TableCell>Display Order</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {regions.map((region) => (
              <TableRow key={region.RegionId}>
                <TableCell>{region.RegionName}</TableCell>
                <TableCell>{region.RegionCode}</TableCell>
                <TableCell>{region.ProviderName}</TableCell>
                <TableCell>{region.IsActive ? 'Yes' : 'No'}</TableCell>
                <TableCell>{region.DisplayOrder}</TableCell>
                <TableCell align="right">
                  <IconButton onClick={() => handleOpenDialog(region)} size="small"><EditIcon fontSize="small" /></IconButton>
                  <IconButton onClick={() => handleDeleteClick(region.RegionId)} size="small" color="error"><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingRegion ? 'Edit Region' : 'Add Region'}</DialogTitle>
        <DialogContent>
          <TextField autoFocus margin="dense" size="small" label="Region Name" fullWidth value={formData.regionName} onChange={(e) => setFormData({ ...formData, regionName: e.target.value })} required />
          <TextField margin="dense" size="small" label="Region Code" fullWidth value={formData.regionCode} onChange={(e) => setFormData({ ...formData, regionCode: e.target.value })} required />
          <TextField select margin="dense" size="small" label="Provider" fullWidth value={formData.providerId} onChange={(e) => setFormData({ ...formData, providerId: e.target.value })} required>
            {providers.map((provider) => (
              <MenuItem key={provider.ProviderId} value={provider.ProviderId}>{provider.ProviderName}</MenuItem>
            ))}
          </TextField>
          <TextField margin="dense" size="small" label="Display Order" type="number" fullWidth value={formData.displayOrder} onChange={(e) => setFormData({ ...formData, displayOrder: parseInt(e.target.value) || 0 })} />
          <FormControlLabel control={<Switch checked={formData.isActive} onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })} />} label="Active" />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} size="small">Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="secondary" disabled={!formData.regionName || !formData.regionCode || !formData.providerId}>
            {editingRegion ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={confirmDialog.open}
        title="Deactivate Region"
        message="Are you sure you want to deactivate this region?"
        confirmLabel="Deactivate"
        confirmColor="error"
        onConfirm={handleDeleteConfirm}
        onCancel={() => setConfirmDialog({ open: false, id: 0 })}
      />
    </Box>
  );
};

export default Regions;
